import qs from 'qs'
import type { DataProvider, Identifier } from 'react-admin'
import { fetchUtils } from 'react-admin'

const apiUrl = import.meta.env.VITE_BACKEND_API_URL

// Options accepted by our fetch wrapper. All internal call sites construct
// Headers (never a plain object).
//
// `__retried` marks the quiet 401 retry. It does NOT provide the loop
// protection this comment used to claim: the retry calls fetchJsonWithTimeout
// DIRECTLY rather than re-entering httpClient, so recursion is structurally
// impossible and no caller ever sets the flag. Deleting `&& !options.__retried`
// leaves the whole suite green — verified, not assumed. It is kept as a
// belt-and-braces marker that would become load-bearing if the retry were ever
// routed back through httpClient.
type HttpOptions = {
  method?: string
  body?: string
  headers?: Headers
  signal?: AbortSignal
  __retried?: boolean
}

const convertPetDobToDateString = (data: { dob?: unknown }) => {
  if (typeof data.dob === 'number') {
    if (data.dob === 0) {
      data.dob = null
    } else {
      const dobDate = new Date(data.dob * 1000)
      if (!Number.isNaN(dobDate.getTime())) {
        data.dob = dobDate.toISOString().split('T')[0]
      }
    }
  }
}

const getToken = () => sessionStorage.getItem('token')

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// A backend that ACCEPTS the connection then stalls (no response, no error)
// leaves a bare fetch() promise unsettled forever: the UI spins permanently
// with no error and no recovery short of a reload. Every request gets a hard
// deadline via AbortController so it is guaranteed to settle one way or
// another. 30s is comfortably above the slowest LEGITIMATE query (the
// metrics list can return up to 50k rows) — do not drop this to something
// aggressive like 5s or large-but-valid queries will false-positive as
// "timed out".
// This is a BACKSTOP against an infinite hang, not a latency SLO — pick it from
// the worst LEGITIMATE request, not from what feels fast.
// Measured against prod 2026-08-03: a single-device metrics fetch (limit 50000)
// is ~2.1-2.5s / 10MB, but the "All Devices" shape is ~8.8s / **26MB**. The
// payload is what matters: 26MB needs ~21s at 10 Mbps and ~42s at 5 Mbps, so an
// earlier 30s value would have converted a slow-but-working load on clinic wifi
// into a hard failure. 90s keeps ~10x headroom over the measured server time
// while still bounding the unsettled-forever case that motivated this (the local
// backend wedged and answered nothing for 20s+ on 2026-08-03).
export const REQUEST_TIMEOUT_MS = 90000

// Wraps fetchUtils.fetchJson with the deadline above. Verified against
// packages/ra-core/src/dataProvider/fetch.ts: fetchJson spreads `options`
// (including `signal`) straight into the underlying fetch() call, so an
// AbortController signal set here is honoured by fetch itself. We still race
// against our own timer (rather than relying solely on abort) so a stalled
// request surfaces a clear, distinguishable error even if the environment's
// fetch is slow to propagate the AbortError.
const fetchJsonWithTimeout = (url: string, options: HttpOptions = {}) => {
  const controller = new AbortController()
  let timeoutId: ReturnType<typeof setTimeout>
  const timeoutPromise = new Promise<never>((_resolve, reject) => {
    timeoutId = setTimeout(() => {
      controller.abort()
      reject(
        new Error(
          `Request timed out after ${REQUEST_TIMEOUT_MS}ms (no response from server): ${url}`,
        ),
      )
    }, REQUEST_TIMEOUT_MS)
  })

  const fetchPromise = fetchUtils.fetchJson(url, { ...options, signal: controller.signal })
  // If the timeout wins the race below, the abort will eventually cause
  // fetchPromise to reject too (once the environment's fetch propagates it).
  //
  // This catch is BELT-AND-BRACES, not the mechanism. `Promise.race` calls
  // `.then()` on every promise it is given, so fetchPromise always has a
  // handler attached and its late rejection is never "unhandled" — measured
  // 2026-08-31 with a control: a promise inside a race produced 0 unhandled
  // rejections without this line, while an orphan promise produced 1. Removing
  // this line changes nothing observable today.
  //
  // It is kept because the protection is a property of the race, and a future
  // refactor that stops racing fetchPromise (awaiting it separately, say) would
  // silently reintroduce the unhandled rejection. Pinned by the VP-DATA-R07
  // tests, which assert the observable invariant rather than this line.
  fetchPromise.catch(() => {})

  return Promise.race([fetchPromise, timeoutPromise]).finally(() => clearTimeout(timeoutId))
}

// ⚠️ THERE ARE TWO 401/403 POLICIES IN THIS APP AND THEY ARE NOT THE SAME.
//   • THIS one (VP-DATA R09-R15) is the TRANSPORT policy: it decides whether a
//     single request is retried, and it NEVER logs anyone out on a 403.
//   • `checkError` in src/authProvider.ts (VP-AUTH R21-R26) is the SESSION
//     policy: it decides whether the user stays logged in.
// Changing one does NOT change the other. Read both before "unifying" them.
//
// Best-effort detection of "safe to retry" requests. We only retry idempotent
// reads (GET, or no method specified which defaults to GET). Mutations are
// never retried because we don't want to risk double-submit on POST/PUT/PATCH.
const isIdempotentRequest = (options: HttpOptions = {}) => {
  const method = (options.method || 'GET').toUpperCase()
  return method === 'GET' || method === 'HEAD'
}

const applyAuthHeader = (options: HttpOptions) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' })
  }
  const token = getToken()
  if (token) {
    // `JWT <token>` — but NOT because Bearer is rejected. Verified against
    // ada-core 2026-08-29: Payload 3.85.1's default `auth.jwtOrder` is
    // ['JWT', 'Bearer', 'cookie'] (payload/dist/config/defaults.js) and
    // ada-core does not override it, so all three are accepted and JWT is
    // simply tried first. The previous comment here claimed "Payload CMS
    // expects JWT prefix, not Bearer", which is false and made the
    // authProvider's `Bearer` calls (/me, logout) look like a latent bug.
    // Keep JWT for consistency with the rest of this module; do not "fix"
    // authProvider to match — both work.
    options.headers.set('Authorization', `JWT ${token}`)
  }
}

// Custom fetch function to add Authorization header and handle auth errors.
//
// Logout-on-401 race protection: when a device detail page mounts, 5+ parallel
// reads can fire (devices/metrics/alerts/etc.) plus a paginated metrics loop.
// A single transient 401 — from a brief network hiccup, a backend hot-reload,
// or a read-replica lag window — used to wipe the token instantly and bounce
// the user back to /login mid-load. We now treat the first 401 on an
// idempotent GET as recoverable: pause briefly, re-attach the latest token
// (in case a sibling request refreshed it), and retry once. Only if the
// retry also returns 401 do we clear the token and let React Admin route
// to the login screen. 403 (authenticated but forbidden from a resource)
// does NOT clear the token — see the detailed note inside httpClient.
const httpClient = (url: string, options: HttpOptions = {}): Promise<{ json: any }> => {
  applyAuthHeader(options)

  return fetchJsonWithTimeout(url, options).catch(async (error: any) => {
    const status = error?.status

    if (status === 401 && isIdempotentRequest(options) && !options.__retried) {
      // Quiet retry-once. Wait a beat for any in-flight token refresh /
      // session re-establishment by sibling requests, then try again with
      // the freshest token from sessionStorage.
      await sleep(300)
      const retryOptions: HttpOptions = { ...options, __retried: true }
      // Rebuild headers from scratch so we pick up any token that arrived
      // between the first attempt and now (and don't reuse a stale Headers
      // object that's already been consumed by fetch).
      const retryHeaders = new Headers({ Accept: 'application/json' })
      retryOptions.headers = retryHeaders
      // Preserve original Content-Type / other headers if the caller set any.
      if (options.headers) {
        const original =
          options.headers instanceof Headers ? options.headers : new Headers(options.headers)
        original.forEach((value, key) => {
          if (key.toLowerCase() !== 'authorization') {
            retryHeaders.set(key, value)
          }
        })
      }
      applyAuthHeader(retryOptions)
      // Fresh call to fetchJsonWithTimeout = a fresh AbortController + timer,
      // not a reuse of the first attempt's (already-expired/aborted) one.
      return fetchJsonWithTimeout(url, retryOptions).catch((retryError: any) => {
        const retryStatus = retryError?.status
        if (retryStatus === 401) {
          // Confirmed authentication failure on the second attempt — token is really gone.
          sessionStorage.removeItem('token')
        }
        // For 403 on retry, do NOT clear token — see note below.
        throw retryError
      })
    }

    // 401 = the JWT itself is invalid/expired → wipe token, let authProvider
    // route to login. 403 = authenticated but forbidden from THIS resource →
    // do NOT wipe the token. Lumping 403 in used to log the vet out the
    // moment any single row in a large list query (e.g. /api/metrics?limit=
    // 10000) failed Payload's row-level access check, even though the user
    // was perfectly logged in. Now we just surface the error to the caller.
    // 5xx, network errors, timeouts: re-throw unchanged so the UI can show
    // a transient-error toast without bouncing to login.
    if (status === 401) {
      sessionStorage.removeItem('token')
      throw error
    }
    throw error
  })
}

const convertFiltersToWhere = (
  rawFilters: Record<string, any>,
  searchableFields: string[] = [],
) => {
  // Work on a COPY. This function consumes keys as it translates them
  // (`delete filters['device[in][0]']` below, and `delete filters.q`) so the
  // generic pass at the end only sees untranslated keys — that pruning is
  // load-bearing. But ra-core closes over ONE filter object per query and
  // re-invokes the same queryFn on every retry/refetch/remount, so deleting
  // the CALLER's keys meant the second request went out with no `where` at
  // all: PetShow was observed issuing
  // `GET /api/metrics?page=1&limit=1&sort=timestamp` against the whole table
  // (totalDocs 3,786,729) instead of the pet's devices, returning another
  // tenant's oldest row. Copying keeps the pruning and stops the mutation.
  // (eng-brain P17-2 / L-027.)
  const filters = { ...rawFilters }
  const where: Record<string, unknown> = {}

  // Handle RA array-style filters like "device[in][0]=36"
  const arrayValues: Record<string, unknown[]> = {}
  Object.keys(filters).forEach((key) => {
    const match = key.match(/^(.+)\[in\]\[\d+\]$/)
    if (match) {
      const field = match[1]
      arrayValues[field] = arrayValues[field] || []
      arrayValues[field].push(filters[key])
    }
  })
  // Emit comma-delimited 'in' filters and remove original keys
  Object.keys(arrayValues).forEach((field) => {
    where[`where[${field}][in]`] = arrayValues[field].join(',')
    arrayValues[field].forEach((_, idx) => {
      delete filters[`${field}[in][${idx}]`]
    })
  })

  // Handle full-text search.
  //
  // `delete filters.q` is OUTSIDE the searchableFields check on purpose. It
  // used to sit inside it, so a resource with no mapping fell through to the
  // generic pass below and shipped `where[q][equals]=<term>` — a predicate on
  // a field that does not exist. Payload rejects that outright:
  //   HTTP 400 QueryError "The following path cannot be queried: q"
  // (verified against the live API 2026-08-31), so the whole list request
  // failed rather than merely ignoring the search. `foods` hit this on every
  // keystroke, because FoodList has an alwaysOn SearchInput and no mapping.
  //
  // q is a UI-level concept this function translates; it is never a real
  // column. So it is consumed unconditionally, and a resource without
  // searchable fields degrades to an unfiltered list instead of a 400.
  if (filters.q) {
    if (searchableFields.length > 0) {
      searchableFields.forEach((field, index) => {
        where[`where[or][${index}][${field}][contains]`] = filters.q
      })
    }
    delete filters.q
  }

  // Handle other filters
  Object.keys(filters).forEach((key) => {
    let value = filters[key]
    // Skip undefined, null, or empty-array filters
    if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
      return
    }
    // Split key to find operator, while supporting multi-token operators like not_equals
    let field = key
    let operator = 'equals'
    // ORDER IS LOAD-BEARING — scanned in array order with a `break`, NOT
    // longest-suffix-first. `not_equals` works only because it sits before
    // `equals`; sorting this alphabetically would silently re-route every
    // `foo_not_equals` filter to operator `equals` on a field named `foo_not`.
    // Pinned by "VP-DATA-R21: the operator array order is load-bearing".
    const supportedOperators = ['not_equals', 'gte', 'lte', 'like', 'equals']

    for (const candidate of supportedOperators) {
      const suffix = `_${candidate}`
      if (key.endsWith(suffix) && key.length > suffix.length) {
        field = key.slice(0, -suffix.length)
        operator = candidate
        break
      }
    }
    const payloadOperator =
      {
        equals: 'equals',
        not_equals: 'not_equals',
        gte: 'greater_than_equal',
        lte: 'less_than_equal',
        like: 'contains',
      }[operator as 'equals' | 'not_equals' | 'gte' | 'lte' | 'like'] || 'equals'

    // Handle object values (e.g. relationship fields)
    if (typeof value === 'object' && value !== null && 'id' in value) {
      value = value.id
    }

    where[`where[${field}][${payloadOperator}]`] = value
  })

  return where
}

const searchableFieldsByResource: Record<string, string[]> = {
  users: ['email', 'role', 'name', 'clinic_name'],
  pets: [
    'name',
    'breed',
    'species',
    'id',
    'owner.name',
    'owner.email',
    'owner.clinic_name',
    'owner.vets.clinic_name',
  ],
  devices: ['name', 'serial', 'type'],
  metrics: [], // No searchable text fields
  // FoodList renders an alwaysOn <SearchInput source="q" />, so this MUST have
  // an entry or every keystroke 400s (see the q-handling comment below).
  // brand + productName are the text columns the list shows. `species` is
  // excluded on purpose: it is an enum and `where[species][contains]` returns
  // HTTP 500 from Payload, so including it would trade a 400 for a 500.
  foods: ['brand', 'productName'],
  activities: ['type', 'description'],
  emrs: ['notes'],
  categories: ['name'],
}

// updateMany/deleteMany fan out one request per id. Promise.all would reject
// the WHOLE batch the moment a single id failed, so react-admin surfaced one
// generic error implying nothing happened — but the other N-1 requests had
// already mutated server state (reachable today via bulk-delete on `groups`
// for non-vet roles). Promise.allSettled + this helper instead report which
// ids actually succeeded (as react-admin's { data: [ids] } contract expects)
// and, if any failed, throw an error that states the truth: how many of how
// many succeeded, and which ids didn't.
/**
 * The ONLY definition of the pets write allowlist (VP-DATA-R37/R38).
 *
 * This 18-field array was duplicated verbatim in `update` and `updateMany`,
 * under a comment claiming "updateMany below uses the same helper so the two
 * paths cannot diverge again" — there was no helper, and they had already
 * diverged twice: once in CONTENT (the chronic-care fields were missing from
 * one list until 2026-07-26, so that whole panel was silently discarded), and
 * once in SEMANTICS (`updateMany` additionally required `!== undefined`).
 *
 * The semantic difference was invisible on the wire — `JSON.stringify` omits
 * undefined properties — but the content drift was not, and that is the one
 * that cost a shipped feature. One array, one picker, both paths.
 */
const PETS_WRITE_ALLOWLIST = [
  'name',
  'species',
  'breed',
  'dob',
  'gender',
  'color',
  'height',
  'weight',
  'glucoserange',
  'weight_kg',
  'neutered',
  'birth_date',
  'body_condition_score',
  'chronic_conditions',
  'target_glucose_low',
  'target_glucose_high',
  'target_kcal_in_override',
  'hide_glucose_values',
] as const

/**
 * Keep only allowlisted pet fields, emitting the CANONICAL spelling.
 *
 * Matching is case-insensitive but the key written is the canonical one: an
 * earlier form checked `key.toLowerCase()` and then forwarded the ORIGINAL key,
 * so `Weight` passed the check and went to the API under a name it does not
 * accept — a silent server-side drop.
 */
const pickAllowedPetFields = (input: Record<string, unknown>): Record<string, unknown> => {
  const picked: Record<string, unknown> = {}
  Object.keys(input).forEach((key) => {
    const canonical = PETS_WRITE_ALLOWLIST.find((field) => field === key.toLowerCase())
    if (canonical && input[key] !== undefined) {
      picked[canonical] = input[key]
    }
  })
  return picked
}

const settleManyResults = (
  resource: string,
  ids: Identifier[],
  results: PromiseSettledResult<Identifier>[],
) => {
  const succeededIds: Identifier[] = []
  const failedIds: Identifier[] = []
  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeededIds.push(result.value)
    } else {
      failedIds.push(ids[index])
    }
  })

  if (failedIds.length > 0) {
    const error = new Error(
      `${succeededIds.length} of ${ids.length} ${resource} requests succeeded; failed: ${failedIds.join(', ')}`,
    ) as Error & { succeededIds: Identifier[]; failedIds: Identifier[] }
    error.succeededIds = succeededIds
    error.failedIds = failedIds
    throw error
  }

  return { data: succeededIds }
}

// Declared against react-admin's DataProvider so every method signature is
// contract-checked at compile time — this file was plain JS (the only
// unlinted, untyped module in src/) and no consumer got better than `any`.
// getCount is a custom extension; the synthesized Content-Range headers on
// list results are asserted by index.test.js, so results carry them via a
// widened return (a variable, not a fresh literal, to satisfy excess-property
// checks).
const dataProvider: DataProvider & {
  getCount: (
    resource: string,
    params?: { filter?: Record<string, unknown> },
  ) => Promise<{ data: { count: number } }>
} = {
  getList: (resource, params) => {
    // RA 5's contract: pagination/sort are OPTIONAL on GetListParams — the
    // JS version destructured them blindly (surfaced by the .ts conversion).
    const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 }
    const { field, order } = params.sort ?? { field: 'createdAt', order: 'DESC' }
    const { filter } = params
    const { meta } = params

    // Use custom search endpoint for pets when 'q' filter is present
    if (resource === 'pets' && filter && filter.q) {
      const searchQuery = filter.q
      const url = `${apiUrl}/pets/search?q=${encodeURIComponent(searchQuery)}`

      return httpClient(url)
        .then(({ json }) => {
          const results = Array.isArray(json) ? json : []
          const data = results.map((doc: any) => ({ ...doc, id: doc.id }))
          data.forEach(convertPetDobToDateString)
          return {
            data,
            total: results.length,
            headers: new Headers({
              'Content-Range': `items 0-${results.length - 1}/${results.length}`,
            }),
          }
        })
        .catch((error) => {
          // Fallback to standard getList with client-side filtering if search endpoint fails
          console.warn('Pets search endpoint failed, falling back to client-side search:', error)

          const standardUrl = `${apiUrl}/${resource}`
          const standardQuery = {
            page: '1',
            limit: '10000', // Fetch all for client-side filtering
            sort: field ? `${order === 'DESC' ? '-' : ''}${field}` : '-createdAt',
          }

          return httpClient(`${standardUrl}?${new URLSearchParams(standardQuery).toString()}`).then(
            ({ json }) => {
              const allPets = json?.docs || []

              // Client-side search across multiple fields
              const searchLower = searchQuery.toLowerCase()
              const filtered = allPets.filter((pet: any) => {
                return (
                  pet.name?.toLowerCase().includes(searchLower) ||
                  pet.breed?.toLowerCase().includes(searchLower) ||
                  pet.species?.toLowerCase().includes(searchLower) ||
                  pet.owner?.name?.toLowerCase().includes(searchLower) ||
                  pet.owner?.email?.toLowerCase().includes(searchLower) ||
                  pet.owner?.clinic_name?.toLowerCase().includes(searchLower) ||
                  pet.id?.toString().includes(searchLower)
                )
              })

              const data = filtered.map((doc: any) => ({ ...doc, id: doc.id }))
              data.forEach(convertPetDobToDateString)
              return {
                data,
                total: filtered.length,
                headers: new Headers({
                  'Content-Range': `items 0-${filtered.length - 1}/${filtered.length}`,
                }),
              }
            },
          )
        })
    }

    const query = {
      page: page,
      limit: perPage,
      sort: `${order === 'DESC' ? '-' : ''}${field}`,
    }

    // Merge any custom query params passed via params.meta.params (e.g., depth=0)
    if (meta?.params) {
      Object.assign(query, meta.params)
    }

    if (filter && Object.keys(filter).length > 0) {
      const searchableFields = searchableFieldsByResource[resource] || []
      Object.assign(query, convertFiltersToWhere(filter, searchableFields))
    }

    const url = `${apiUrl}/${resource}?${qs.stringify(query, { encode: false })}`

    return httpClient(url).then(({ json }) => {
      const start = (json.page - 1) * json.limit
      const end = start + json.docs.length - 1
      const docs = json.docs.map((doc: any) => ({ ...doc, id: doc.id }))
      if (resource === 'pets') docs.forEach(convertPetDobToDateString)
      return {
        data: docs,
        total: json.totalDocs,
        headers: new Headers({ 'Content-Range': `items ${start}-${end}/${json.totalDocs}` }),
      }
    })
  },

  getCount: (resource, params = {}) => {
    const query = {
      page: 1,
      limit: 1, // Minimize data fetched, we only need totalDocs
    }
    if (params.filter && Object.keys(params.filter).length > 0) {
      const searchableFields = searchableFieldsByResource[resource] || []
      Object.assign(query, convertFiltersToWhere(params.filter, searchableFields))
    }

    const url = `${apiUrl}/${resource}?${qs.stringify(query, { encode: false })}`
    return httpClient(url).then(({ json }) => ({
      data: { count: json.totalDocs },
    }))
  },

  getOne: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => {
      const data = { ...json, id: json.id }

      if (resource === 'pets') {
        convertPetDobToDateString(data)
      }

      return { data }
    }),

  getMany: (resource, params) => {
    // An empty id set has nothing to fetch, and it MUST short-circuit: Payload
    // reads `limit=0` as UNLIMITED (measured — GET /api/pets?limit=0 returned
    // all 320 rows), so passing `params.ids.length` straight through would turn
    // getMany([]) into a full-collection download.
    if (params.ids.length === 0) return Promise.resolve({ data: [] })

    const query = {
      'where[id][in]': params.ids.join(','),
      // Payload's default page size is 10 (measured against the live API
      // 2026-08-31: GET /api/pets returns limit=10). Without an explicit limit
      // a getMany for 15 ids returned 10 docs with totalDocs=15 — the tail was
      // silently dropped, and a missing record is indistinguishable from one
      // that does not exist. React Admin resolves a whole page of
      // ReferenceFields through getMany, so a 25-row list could resolve only
      // ten of its references and simply render the rest blank.
      //
      // Ask for exactly as many rows as ids: the caller already named every id,
      // so this cannot fetch more than it asked for.
      limit: params.ids.length,
    }
    const url = `${apiUrl}/${resource}?${qs.stringify(query, { encode: false })}`
    return httpClient(url).then(({ json }) => {
      const docs = json.docs.map((doc: any) => ({ ...doc, id: doc.id }))
      if (resource === 'pets') docs.forEach(convertPetDobToDateString)
      return { data: docs }
    })
  },

  getManyReference: (resource, params) => {
    const { page, perPage } = params.pagination ?? { page: 1, perPage: 25 }
    const { field, order } = params.sort ?? { field: 'createdAt', order: 'DESC' }
    const query = {
      page: page,
      limit: perPage,
      sort: `${order === 'DESC' ? '-' : ''}${field}`,
      [params.target]: params.id, // Reference field
    }
    const url = `${apiUrl}/${resource}?${qs.stringify(query, { encode: false })}`
    return httpClient(url).then(({ json }) => {
      const docs = json.docs.map((doc: any) => ({ ...doc, id: doc.id }))
      if (resource === 'pets') docs.forEach(convertPetDobToDateString)
      return { data: docs, total: json.totalDocs }
    })
  },

  create: (resource, params) =>
    httpClient(`${apiUrl}/${resource}`, {
      method: 'POST',
      body: JSON.stringify(params.data),
    }).then(({ json }) => {
      // Payload CMS wraps responses in { doc: {...} }
      const data = json.doc || json
      return { data: { ...data, id: data.id } }
    }),

  update: (resource, params) => {
    let data = params.data

    // For pets, only allow specific fields that the API accepts
    if (resource === 'pets') {
      // Allow veterinarians to edit basic pet info + measurements
      // Exclude complex objects like profile/Profile that may cause backend issues
      data = pickAllowedPetFields(params.data)

      // Convert DOB to Unix timestamp in seconds for backend storage
      // DateInput sends strings like "2024-01-01", but backend expects Unix timestamp in seconds
      if (data.dob !== undefined && data.dob !== null && data.dob !== '') {
        let dobTimestamp = null

        if (typeof data.dob === 'string' && data.dob.trim() !== '') {
          // Parse date string (YYYY-MM-DD format from DateInput)
          const dobDate = new Date(data.dob)
          if (!Number.isNaN(dobDate.getTime())) {
            dobTimestamp = Math.floor(dobDate.getTime() / 1000)
          }
        } else if (data.dob instanceof Date) {
          // Handle Date object
          if (!Number.isNaN(data.dob.getTime())) {
            dobTimestamp = Math.floor(data.dob.getTime() / 1000)
          }
        } else if (typeof data.dob === 'number' && data.dob > 0) {
          // Already a valid timestamp (non-zero), keep it
          dobTimestamp = data.dob
        }

        if (dobTimestamp !== null && dobTimestamp > 0) {
          data.dob = dobTimestamp
        } else {
          console.warn('Invalid DOB value, removing:', data.dob)
          delete data.dob // Remove invalid DOB to prevent data corruption
        }
      } else if (data.dob === '' || data.dob === null) {
        // User cleared the date field - set to null (not 0)
        data.dob = null
      }
    }

    return httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: resource === 'pets' || resource === 'alert-rules' ? 'PATCH' : 'PUT',
      body: JSON.stringify(data),
    }).then(({ json }) => {
      // Payload CMS wraps responses in { doc: {...} }
      const responseData = json.doc || json

      if (resource === 'pets') convertPetDobToDateString(responseData)

      return { data: { ...responseData, id: responseData.id } }
    })
  },

  updateMany: (resource, params) => {
    let data = params.data

    // For pets, only allow specific fields that the API accepts
    if (resource === 'pets') {
      // Allow veterinarians to edit basic pet info + measurements
      data = pickAllowedPetFields(params.data)
    }

    const ids = params.ids
    const promises = ids.map((id) =>
      httpClient(`${apiUrl}/${resource}/${id}`, {
        method: resource === 'pets' || resource === 'alert-rules' ? 'PATCH' : 'PUT',
        body: JSON.stringify(data),
      }).then(({ json }) => json.id),
    )
    return Promise.allSettled(promises).then((results) => settleManyResults(resource, ids, results))
  },

  delete: (resource, params) =>
    httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: 'DELETE',
    }).then(({ json }) => ({
      data: { ...json, id: json.id },
    })),

  deleteMany: (resource, params) => {
    const ids = params.ids
    const promises = ids.map((id) =>
      httpClient(`${apiUrl}/${resource}/${id}`, {
        method: 'DELETE',
      }).then(({ json }) => json.id),
    )
    return Promise.allSettled(promises).then((results) => settleManyResults(resource, ids, results))
  },
}

export default dataProvider
